import React from 'react';
import { Platform } from '../../../types/Platforms';
import { isTikTokPostingAvailable } from '../../tiktok/utils/tiktokOptions';
import type { ConnectedProfile } from '../../../types/customer';
import { VIDEO_PLATFORMS, getPlatformLabel, getPlatformColor, getPlatformIcon } from '../types';

interface VideoPlatformSelectorProps {
  selectedPlatforms: Platform[];
  onTogglePlatform: (platform: Platform) => void;
  connectedPlatforms?: string[];
  /** TikTok is offered only when configured and this customer connected an account (SGL-170). */
  connectedProfiles?: ConnectedProfile[];
}

export const VideoPlatformSelector: React.FC<VideoPlatformSelectorProps> = ({
  selectedPlatforms,
  onTogglePlatform,
  connectedPlatforms,
  connectedProfiles,
}) => {
  const tiktokAvailable = isTikTokPostingAvailable(connectedProfiles);
  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-[#2f4941]">
        Select Platforms
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {VIDEO_PLATFORMS.filter((p) => p !== Platform.TIKTOK_REELS || tiktokAvailable).map((platform) => {
          const isSelected = selectedPlatforms.includes(platform);
          const isConnected = !connectedPlatforms || connectedPlatforms.includes(platform);

          return (
            <button
              key={platform}
              type="button"
              onClick={() => onTogglePlatform(platform)}
              className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                isSelected
                  ? 'border-[#2f4941] bg-[#2f4941]/10'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              {/* Platform icon badge */}
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm ${getPlatformColor(
                  platform
                )}`}
              >
                <span className="material-icons text-sm">
                  {getPlatformIcon(platform)}
                </span>
              </div>

              {/* Label */}
              <div className="flex-1 text-left">
                <span
                  className={`text-sm font-medium ${
                    isSelected ? 'text-[#2f4941]' : 'text-gray-700'
                  }`}
                >
                  {getPlatformLabel(platform)}
                </span>
                {!isConnected && (
                  <span className="block text-xs text-amber-600">Not connected</span>
                )}
              </div>

              {/* Checkbox */}
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                  isSelected
                    ? 'bg-[#2f4941] border-[#2f4941]'
                    : 'border-gray-300'
                }`}
              >
                {isSelected && (
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default VideoPlatformSelector;
